<script lang="ts">
  /* Персональный онбординг (spec 20261001 §8) — the one piece of personal
     work the platform sells. Everything else is self-service, so nothing here
     may promise support beyond this paid, time-boxed service.

     `variant="full"` (Settings → «Начало работы»): what is included, what to
     prepare, what is not included, and the request form — then, once asked,
     the order's progress as the operator ticks it in the Backoffice.
     `variant="offer"`: one line with a link to the full card, for the wizard's
     closing screen. */
  import { onMount, tick } from "svelte";
  import { _ } from "svelte-i18n";
  import { push, router } from "svelte-spa-router";
  import { auth } from "$lib/auth";
  import { ApiError } from "$lib/api";
  import { formatDate, formatEur } from "$lib/format";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import {
    ONBOARDING_STEPS,
    SERVICE_STATUS_LABEL,
    fetchOverview,
    listServices,
    onboardingCardHref,
    priceOf,
    requestOnboarding,
    type ServiceOrder,
  } from "$lib/billingRecap";

  let {
    slug,
    variant = "full",
    onopen,
  }: {
    slug: string;
    variant?: "full" | "offer";
    /** Offer variant: called instead of navigating, so the wizard can finish first. */
    onopen?: () => void;
  } = $props();

  const isOwner = $derived($auth.memberships.find((m) => m.tenant.slug === slug)?.role === "owner");

  let price = $state(499);
  let orders = $state<ServiceOrder[]>([]);
  let loaded = $state(false);
  let cardEl = $state<HTMLElement | null>(null);

  let contactName = $state("");
  let contactPhone = $state("");
  let comment = $state("");
  let sending = $state(false);
  let error = $state("");

  /** The order to show: the latest one that is not cancelled. */
  const current = $derived(orders.find((o) => o.code === "onboarding" && o.status !== "cancelled") ?? null);

  onMount(async () => {
    contactName = $auth.user?.full_name ?? "";
    contactPhone = $auth.user?.phone ?? "";
    try {
      const ov = await fetchOverview(slug);
      price = priceOf("onboarding", ov.price_book) ?? price;
    } catch {
      /* the list price stays as written */
    }
    if (variant !== "full") return;
    try {
      orders = await listServices(slug);
    } catch {
      /* no access to the list: the card still describes the service */
    }
    loaded = true;
    // Arrived from the recap screen or the wizard: bring the card into view.
    if (new URLSearchParams(router.querystring ?? "").get("service") === "onboarding") {
      await tick();
      cardEl?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!isOwner || sending) return;
    sending = true;
    error = "";
    try {
      const order = await requestOnboarding(slug, {
        contact_name: contactName.trim(),
        contact_phone: contactPhone.trim(),
        comment: comment.trim(),
      });
      orders = [order, ...orders];
    } catch (err) {
      if (err instanceof ApiError && err.code === "SERVICE_OPEN") {
        // One is already open: show it instead of a second request.
        try {
          orders = await listServices(slug);
        } catch {
          error = err.message;
        }
      } else {
        error = (err as Error)?.message ?? $_("billing.onb.form.error");
      }
    } finally {
      sending = false;
    }
  }

  function openFull() {
    if (onopen) onopen();
    else push(onboardingCardHref(slug));
  }

  const STAGES = ["requested", "invoiced", "in_progress", "done"] as const;
  const stageIndex = $derived(current ? STAGES.indexOf(current.status as (typeof STAGES)[number]) : -1);
  const stepsDone = $derived(current ? current.checklist.filter((c) => c.done).length : 0);
  const doneAt = (id: string) => current?.checklist.find((c) => c.id === id) ?? null;
  const hoursSpent = $derived(current ? Number(current.hours_spent) || 0 : 0);

  const PREP_ROWS: { id: string; whatKey: string; formKey: string; needKey: string }[] = [
    { id: "requisites", whatKey: "billing.onb.prep.requisites.what", formKey: "billing.onb.prep.requisites.form", needKey: "billing.onb.prep.need.required" },
    { id: "logo", whatKey: "billing.onb.prep.logo.what", formKey: "billing.onb.prep.logo.form", needKey: "billing.onb.prep.need.wanted" },
    { id: "products", whatKey: "billing.onb.prep.products.what", formKey: "billing.onb.prep.products.form", needKey: "billing.onb.prep.need.required" },
    { id: "photos", whatKey: "billing.onb.prep.photos.what", formKey: "billing.onb.prep.photos.form", needKey: "billing.onb.prep.need.wanted" },
    { id: "prices", whatKey: "billing.onb.prep.prices.what", formKey: "billing.onb.prep.prices.form", needKey: "billing.onb.prep.need.sales" },
    { id: "partners", whatKey: "billing.onb.prep.partners.what", formKey: "billing.onb.prep.partners.form", needKey: "billing.onb.prep.need.required" },
    { id: "stock", whatKey: "billing.onb.prep.stock.what", formKey: "billing.onb.prep.stock.form", needKey: "billing.onb.prep.need.stock" },
    { id: "staff", whatKey: "billing.onb.prep.staff.what", formKey: "billing.onb.prep.staff.form", needKey: "billing.onb.prep.need.required" },
    { id: "public_page", whatKey: "billing.onb.prep.public_page.what", formKey: "billing.onb.prep.public_page.form", needKey: "billing.onb.prep.need.wanted" },
    { id: "samples", whatKey: "billing.onb.prep.samples.what", formKey: "billing.onb.prep.samples.form", needKey: "billing.onb.prep.need.wanted" },
  ];
</script>

{#if variant === "offer"}
  <p class="onb-offer">
    <span class="material-icons-outlined" aria-hidden="true">support_agent</span>
    <span>
      {$_("billing.onb.offer", { values: { price: formatEur(price, 0) } })}
      <button type="button" class="link" onclick={openFull}>{$_("billing.onb.offerMore")}</button>
    </span>
  </p>
{:else}
  <section class="onb-card card" bind:this={cardEl} id="personal-onboarding" aria-labelledby="onb-card-title">
    <div class="onb-head">
      <span class="material-icons-outlined onb-icon" aria-hidden="true">support_agent</span>
      <div>
        <h2 id="onb-card-title">{$_("billing.onb.title", { values: { price: formatEur(price, 0) } })}</h2>
        <p class="onb-lead">{$_("billing.onb.lead")}</p>
      </div>
    </div>

    {#if current}
      <!-- The order's progress, as the operator records it. -->
      <div class="onb-status">
        <ol class="stages">
          {#each STAGES as st, i (st)}
            <li class:done={i < stageIndex} class:now={i === stageIndex}>
              <span class="stage-dot" aria-hidden="true">
                {#if i < stageIndex || (i === stageIndex && st === "done")}<span class="material-icons-outlined">check</span>{:else}{i + 1}{/if}
              </span>
              {$_(SERVICE_STATUS_LABEL[st])}
            </li>
          {/each}
        </ol>
        <dl class="facts">
          <div><dt>{$_("billing.onb.requestedOn")}</dt><dd>{formatDate(current.created_at)}</dd></div>
          <div>
            <dt>{$_("billing.onb.hours")}</dt>
            <dd class="num">{$_("billing.onb.hoursValue", { values: { spent: hoursSpent, included: current.hours_included } })}</dd>
          </div>
          <div><dt>{$_("billing.onb.validUntil")}</dt><dd>{current.expires_on ? formatDate(current.expires_on) : "-"}</dd></div>
          <div><dt>{$_("billing.onb.stepsDone")}</dt><dd class="num">{stepsDone} / {ONBOARDING_STEPS.length}</dd></div>
        </dl>
        <ol class="checklist">
          {#each ONBOARDING_STEPS as step (step.id)}
            {@const row = doneAt(step.id)}
            <li class:done={row?.done}>
              <span class="tick material-icons-outlined" aria-hidden="true">{row?.done ? "check_circle" : "radio_button_unchecked"}</span>
              <span class="step-name">{$_(step.labelKey)}</span>
              {#if row?.done && row.done_at}<span class="step-date">{formatDate(row.done_at)}</span>{/if}
            </li>
          {/each}
        </ol>
      </div>
    {:else}
      <h3 class="block-title">{$_("billing.onb.included")}</h3>
      <ol class="steps">
        {#each ONBOARDING_STEPS as step (step.id)}
          <li>
            <span class="step-name">{$_(step.labelKey)}</span>
            <span class="step-desc">{$_(step.descKey)}</span>
          </li>
        {/each}
      </ol>
    {/if}

    <details class="prepare" id="onboarding-prepare">
      <summary>
        <span class="material-icons-outlined" aria-hidden="true">folder_open</span>
        {$_("billing.onb.prep.title")}
        <span class="material-icons-outlined chevron" aria-hidden="true">expand_more</span>
      </summary>
      <p class="prep-intro">{$_("billing.onb.prep.intro")}</p>
      <div class="table-wrap">
        <table class="prep-table" use:resizableColumns={"onboarding-prepare"}>
          <thead>
            <tr>
              <th>{$_("billing.onb.prep.col.what")}</th>
              <th>{$_("billing.onb.prep.col.form")}</th>
              <th>{$_("billing.onb.prep.col.need")}</th>
            </tr>
          </thead>
          <tbody>
            {#each PREP_ROWS as row (row.id)}
              <tr>
                <td class="what">{$_(row.whatKey)}</td>
                <td>
                  {$_(row.formKey)}
                  {#if row.id === "products"}
                    <small>{$_("billing.onb.prep.products.required")}</small>
                    <small>{$_("billing.onb.prep.products.wanted")}</small>
                    <small>{$_("billing.onb.prep.products.optional")}</small>
                    <small><a href={`#/t/${slug}/products/import`}>{$_("billing.onb.prep.products.template")}</a></small>
                  {/if}
                </td>
                <td>{$_(row.needKey)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <p class="prep-note">{$_("billing.onb.prep.note")}</p>
    </details>

    <h3 class="block-title">{$_("billing.onb.excluded")}</h3>
    <ul class="excluded">
      <li>{$_("billing.onb.excl.dev")}</li>
      <li>{$_("billing.onb.excl.content")}</li>
      <li>{$_("billing.onb.excl.after")}</li>
      <li>{$_("billing.onb.excl.limits")}</li>
    </ul>
    <p class="terms">{$_("billing.onb.terms")}</p>

    {#if !current && loaded}
      {#if isOwner}
        <form class="onb-form" onsubmit={submit}>
          <h3 class="block-title">{$_("billing.onb.form.title")}</h3>
          <div class="form-pair">
            <div class="field">
              <label for="onb-contact">{$_("billing.onb.form.contact")}</label>
              <input id="onb-contact" type="text" maxlength="120" bind:value={contactName} />
            </div>
            <div class="field">
              <label for="onb-phone">{$_("billing.onb.form.phone")}</label>
              <input id="onb-phone" type="tel" maxlength="40" bind:value={contactPhone} />
            </div>
          </div>
          <div class="field">
            <label for="onb-comment">{$_("billing.onb.form.comment")}</label>
            <textarea id="onb-comment" rows="3" maxlength="2000" bind:value={comment}></textarea>
          </div>
          {#if error}<p class="onb-error" role="alert">{error}</p>{/if}
          <div class="form-actions">
            <button type="submit" class="btn btn--primary" disabled={sending || !contactName.trim()}>
              {sending ? $_("common.sending") : $_("billing.onb.form.submit", { values: { price: formatEur(price, 0) } })}
            </button>
            <span class="form-hint">{$_("billing.onb.form.hint")}</span>
          </div>
        </form>
      {:else}
        <p class="terms">{$_("billing.onb.ownerOnly")}</p>
      {/if}
    {/if}
  </section>
{/if}

<style>
  .onb-offer {
    display: flex; align-items: flex-start; gap: 8px; margin: 18px 0 0;
    font-size: 13.5px; color: var(--color-text-muted); text-align: left;
  }
  .onb-offer .material-icons-outlined { font-size: 20px; color: var(--color-primary); }
  .link {
    border: 0; background: none; padding: 0; font: inherit; color: var(--color-primary);
    cursor: pointer; text-decoration: underline; text-underline-offset: 2px;
  }

  .onb-card { margin-top: 16px; padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; }
  .onb-head { display: flex; align-items: flex-start; gap: 12px; }
  .onb-icon { font-size: 30px; color: var(--color-primary); flex-shrink: 0; }
  .onb-head h2 { margin: 0; font-size: 17px; font-weight: 700; color: var(--color-surface-darker); }
  .onb-lead { margin: 4px 0 0; font-size: 13.5px; color: var(--color-text-muted); line-height: 1.5; }
  .block-title { margin: 6px 0 0; font-size: 14px; font-weight: 700; color: var(--color-surface-darker); }

  .steps { margin: 0; padding-left: 22px; display: flex; flex-direction: column; gap: 6px; font-size: 13.5px; }
  .steps li { line-height: 1.45; }
  .step-name { font-weight: 600; color: var(--color-surface-darker); }
  .step-desc { display: block; color: var(--color-text-muted); font-size: 12.5px; }

  .prepare { border: 1px solid var(--color-divider); border-radius: var(--radius); padding: 0 14px; }
  .prepare summary {
    display: flex; align-items: center; gap: 8px; padding: 11px 0; cursor: pointer;
    font-size: 14px; font-weight: 600; color: var(--color-primary); list-style: none;
  }
  .prepare summary::-webkit-details-marker { display: none; }
  .prepare .chevron { margin-left: auto; color: var(--color-text-muted); transition: transform 0.15s; }
  .prepare[open] .chevron { transform: rotate(180deg); }
  .prep-intro, .prep-note { margin: 0 0 10px; font-size: 13px; color: var(--color-text-muted); line-height: 1.5; }
  .table-wrap { width: 100%; overflow-x: auto; margin-bottom: 10px; }
  .prep-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .prep-table th {
    text-align: left; font-size: 12px; font-weight: 600; color: var(--color-text-muted);
    padding: 6px 8px; border-bottom: 1px solid var(--color-border);
  }
  .prep-table td { padding: 8px; border-top: 1px solid var(--color-divider); vertical-align: top; line-height: 1.45; }
  .prep-table td.what { font-weight: 600; color: var(--color-surface-darker); }
  .prep-table small { display: block; margin-top: 4px; font-size: 12px; color: var(--color-text-muted); }
  .prep-table a { color: var(--color-primary); }

  .excluded { margin: 0; padding-left: 20px; font-size: 13px; line-height: 1.6; color: var(--color-text); }
  .terms { margin: 0; font-size: 12.5px; color: var(--color-text-muted); }

  .onb-form { display: flex; flex-direction: column; gap: 10px; border-top: 1px solid var(--color-divider); padding-top: 12px; }
  .form-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 560px) { .form-pair { grid-template-columns: 1fr; } }
  .form-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
  .form-actions .btn { cursor: pointer; }
  .form-hint { font-size: 12.5px; color: var(--color-text-muted); }
  .onb-error { margin: 0; font-size: 13px; color: var(--color-danger); }

  .onb-status { display: flex; flex-direction: column; gap: 12px; }
  .stages { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: 13px; color: var(--color-text-muted); }
  .stages li { display: inline-flex; align-items: center; gap: 6px; }
  .stages li.now { color: var(--color-primary); font-weight: 700; }
  .stages li.done { color: var(--color-success); }
  .stage-dot {
    width: 22px; height: 22px; border-radius: 50%; display: inline-grid; place-items: center;
    background: var(--color-bg); font-size: 11px; font-weight: 700;
  }
  .stages li.now .stage-dot { background: var(--color-primary); color: #fff; }
  .stages li.done .stage-dot { background: var(--color-success); color: #fff; }
  .stage-dot .material-icons-outlined { font-size: 14px; }
  .facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 0; }
  @media (max-width: 700px) { .facts { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  .facts div { background: var(--color-bg); border-radius: var(--radius); padding: 8px 10px; }
  .facts dt { font-size: 11.5px; color: var(--color-text-muted); }
  .facts dd { margin: 2px 0 0; font-size: 14px; font-weight: 700; color: var(--color-surface-darker); }
  .checklist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; font-size: 13.5px; }
  .checklist li { display: flex; align-items: center; gap: 8px; }
  .checklist .tick { font-size: 18px; color: var(--color-text-muted); }
  .checklist li.done .tick { color: var(--color-success); }
  .checklist li.done .step-name { color: var(--color-text-muted); }
  .step-date { margin-left: auto; font-size: 12px; color: var(--color-text-muted); font-variant-numeric: tabular-nums; }
</style>
